import axios from 'axios';
import * as XLSX from 'xlsx';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { config } from '../config/environment';

// Marketplace API URL from centralized config
const MARKETPLACE_API_URL = config.api.marketplaceUrl;

export interface TelstraDistributorConfig {
  lastUploadAt: string | null;
  lastUploadFileName: string | null;
  productCount: number | null;
}

/**
 * Product from Telstra Excel (deduplicated by SKU - first row wins)
 */
export interface TelstraDistributorProduct {
  sku: string;
  name: string;
  rrpIncGst: number;
  soh: number;
  lastUpdated: string;
}

export interface TelstraProductsResponse {
  uploadedAt: string | null;
  products: TelstraDistributorProduct[];
}

export interface TelstraSeedSku {
  sku: string;
  name: string | null;
  enabled: boolean;
}

export interface TelstraSeedSkuConfig {
  createdAt: string;
  updatedAt: string | null;
  skus: TelstraSeedSku[];
}

export interface TelstraImportResult {
  success: boolean;
  importedCount: number;
  updatedCount: number;
  error: string | null;
  priceChanges?: TelstraPriceChange[];
  stockChanges?: TelstraStockChange[];
}

/**
 * Price change record for sync history
 */
export interface TelstraPriceChange {
  sku: string;
  productName: string;
  oldPrice: number;
  newPrice: number;
}

/**
 * Stock change record for sync history
 */
export interface TelstraStockChange {
  sku: string;
  productName: string;
  oldSoh: number | null;
  newSoh: number | null;
}

/**
 * Sync history entry
 */
export interface TelstraSyncHistoryEntry {
  id: string;
  syncedAt: string;
  triggerType: 'manual' | 'upload';
  fileName: string | null;
  productsMatched: number;
  productsUpdated: number;
  success: boolean;
  error: string | null;
  priceChanges: TelstraPriceChange[];
  stockChanges: TelstraStockChange[];
}

/**
 * Sync history response
 */
export interface TelstraSyncHistoryResponse {
  createdAt: string;
  updatedAt: string | null;
  entries: TelstraSyncHistoryEntry[];
}

export interface TelstraImportOptions {
  deviceEligibility: 'outright_only' | 'mobile_voice' | 'mobile_broadband';
}

async function getAuthenticatedClient() {
  const token = await ensureFreshAitryxCoreToken();
  return axios.create({
    baseURL: MARKETPLACE_API_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
}

function parseSOH(value: string | number | undefined | null): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Math.floor(value);
  const str = String(value).trim();
  if (!str || str === '0') return 0;
  const cleaned = str.replace(/^<=?/, '').trim();
  return parseInt(cleaned, 10) || 0;
}

function parsePrice(value: string | number | undefined | null): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Math.round(value * 100) / 100;
  const str = String(value).trim();
  const cleaned = str.replace(/[$,]/g, '').trim();
  return parseFloat(cleaned) || 0;
}

/**
 * Parse Telstra Excel - DEDUPLICATES by SKU (first row for each SKU wins)
 * - Column B: SKU (UPOS)
 * - Column D: Name (Item Description)
 * - Column E: SOH (SOH Range)
 * - Column J: Price (RRP inc GST)
 */
export function parseTelstraExcel(file: File): Promise<TelstraDistributorProduct[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][];
        
        if (rawData.length < 2) {
          reject(new Error('Spreadsheet appears to be empty'));
          return;
        }
        
        // Find header row
        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(10, rawData.length); i++) {
          const row = rawData[i] as (string | undefined)[];
          if (row && row[1] && String(row[1]).toUpperCase().includes('UPOS')) {
            headerRowIndex = i;
            break;
          }
        }
        
        if (headerRowIndex === -1) {
          reject(new Error('Could not find header row with UPOS in column B'));
          return;
        }
        
        const seenSkus = new Set<string>();
        const products: TelstraDistributorProduct[] = [];
        const now = new Date().toISOString();
        
        // Process rows - FIRST occurrence of each SKU wins, duplicates ignored
        for (let i = headerRowIndex + 1; i < rawData.length; i++) {
          const row = rawData[i] as (string | number | undefined)[];
          if (!row) continue;
          
          const sku = row[1] ? String(row[1]).trim() : '';
          if (!sku) continue;
          
          // Skip if we've already seen this SKU
          if (seenSkus.has(sku)) continue;
          seenSkus.add(sku);
          
          const name = row[3] ? String(row[3]).trim() : '';
          const soh = parseSOH(row[4]);
          const rrpIncGst = parsePrice(row[9]);
          
          products.push({ sku, name, rrpIncGst, soh, lastUpdated: now });
        }
        
        resolve(products);
      } catch (err) {
        reject(new Error('Failed to parse Excel file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}

export async function getTelstraConfig(): Promise<TelstraDistributorConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/telstra/config');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { lastUploadAt: null, lastUploadFileName: null, productCount: null };
    }
    throw new Error(error.response?.data?.error || 'Failed to get Telstra configuration');
  }
}

export async function getTelstraProducts(): Promise<TelstraProductsResponse> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/telstra/products');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { uploadedAt: null, products: [] };
    }
    throw new Error(error.response?.data?.error || 'Failed to get Telstra products');
  }
}

export async function getTelstraSeedSkus(): Promise<TelstraSeedSkuConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/telstra/skus/seed');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { createdAt: new Date().toISOString(), updatedAt: null, skus: [] };
    }
    throw new Error(error.response?.data?.error || 'Failed to get Telstra seed SKUs');
  }
}

export async function saveTelstraSeedSkus(skus: TelstraSeedSku[]): Promise<void> {
  const client = await getAuthenticatedClient();
  await client.post('/telstra/skus/seed', skus);
}

export async function uploadTelstraProducts(
  products: TelstraDistributorProduct[],
  fileName: string
): Promise<{ success: boolean; productCount: number }> {
  const client = await getAuthenticatedClient();
  const response = await client.post('/telstra/products', {
    products,
    fileName,
    uploadedAt: new Date().toISOString()
  });
  return response.data;
}

export async function importTelstraProducts(
  products: TelstraDistributorProduct[],
  options: TelstraImportOptions
): Promise<TelstraImportResult> {
  const client = await getAuthenticatedClient();
  const response = await client.post('/telstra/import', {
    products: products.map(p => ({
      sku: p.sku,
      name: p.name,
      rrpIncGst: p.rrpIncGst,
      soh: p.soh
    })),
    domain: 'mobile-hardware',
    source: 'telstra-distributor',
    deviceEligibility: options.deviceEligibility
  });
  return response.data;
}

export async function deleteTelstraProducts(skus: string[]): Promise<void> {
  const client = await getAuthenticatedClient();
  await client.delete('/telstra/products', { data: { skus } });
}

/**
 * Sync only price (RRP) and stock (SOH) for tracked SKUs from uploaded products.
 * This preserves all manually entered data (name, brand, image, vendor, etc.)
 * by matching on SKU and only updating the price and soh fields.
 * Also records sync history with price/stock changes.
 */
export async function syncTrackedSkusPriceAndStock(
  trackedSkus: TelstraSeedSku[],
  uploadedProducts: TelstraDistributorProduct[],
  fileName?: string
): Promise<TelstraImportResult> {
  // Build lookup of uploaded products by SKU
  const uploadedMap = new Map<string, TelstraDistributorProduct>();
  uploadedProducts.forEach(p => uploadedMap.set(p.sku.toUpperCase(), p));

  // Find all enabled tracked SKUs that have matching uploaded data
  const productsToSync: TelstraDistributorProduct[] = [];
  for (const tracked of trackedSkus) {
    if (!tracked.enabled) continue;
    const uploaded = uploadedMap.get(tracked.sku.toUpperCase());
    if (uploaded) {
      // Only send SKU, price, and SOH - backend will only update these fields
      productsToSync.push({
        sku: uploaded.sku,
        name: uploaded.name, // Backend ignores this for existing products
        rrpIncGst: uploaded.rrpIncGst,
        soh: uploaded.soh,
        lastUpdated: uploaded.lastUpdated
      });
    }
  }

  if (productsToSync.length === 0) {
    return {
      success: false,
      importedCount: 0,
      updatedCount: 0,
      error: 'No tracked SKUs found in uploaded spreadsheet'
    };
  }

  // Call the import endpoint with trackChanges flag - it will only update price and SOH for existing products
  const client = await getAuthenticatedClient();
  const response = await client.post('/telstra/import', {
    products: productsToSync.map(p => ({
      sku: p.sku,
      name: p.name,
      rrpIncGst: p.rrpIncGst,
      soh: p.soh
    })),
    domain: 'mobile-hardware',
    source: 'telstra-distributor',
    trackChanges: true,
    fileName: fileName || null
    // Don't set deviceEligibility - preserve existing setting
  });
  return response.data;
}

/**
 * Get Telstra sync history for the current partner
 */
export async function getTelstraSyncHistory(): Promise<TelstraSyncHistoryResponse> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/telstra/sync/history');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { createdAt: new Date().toISOString(), updatedAt: null, entries: [] };
    }
    throw new Error(error.response?.data?.error || 'Failed to get sync history');
  }
}
